// Design approval (DR3): one submission, three reviews in parallel (MSPPA s3.3, s3.6;
// EUSDR Exhibit 9). On a building's timeline it carries that building's dates.
import { addBusinessDays, addDays, formatDate } from "./dates";
import { PartyChip } from "./PartyChip";
import styles from "./amazonOverview.module.css";

const When = ({ at, otherwise }: { at: Date | null; otherwise: string }) => (
  <span className={styles.when}>{at ? formatDate(at) : otherwise}</span>
);

export const Dr3Panel = ({
  siteCode,
  submitted,
  freehold = false,
}: {
  /** The building whose dates these are; null on the path map. */
  siteCode: string | null;
  /** When the detailed design goes in, as the building's timeline projects it. */
  submitted: Date | null;
  /** Amazon owns the building: Amazon Real Estate approves in place of a landlord. */
  freehold?: boolean;
}) => {
  const review = submitted ? addBusinessDays(submitted, 90) : null;
  const approval = submitted ? addBusinessDays(submitted, 104) : null;
  const installFrom = approval ? addDays(approval, 30) : null;
  return (
    <div className={styles.dr3}>
      <div>
        <div className={styles.eyebrow}>
          {siteCode ? `${siteCode} · ` : ""}Design approval (DR3)
        </div>
        <h3>One submission, three reviews in parallel</h3>
        <p className={styles.sub}>
          Our detailed design submission starts all three reviews on the same
          day. Each raises issues in BIM360 and we close every one. Amazon's
          approval needs all three.
        </p>
      </div>
      <div className={styles.condition}>
        <b>Before Amazon can approve:</b> we hand over the list of every
        government approval the build needs, with a copy of each, including the
        DNO approvals. MSPPA s3.3(b).
      </div>
      <div className={styles.drFlow}>
        <div className={`${styles.drCard} ${styles.ownInrange}`}>
          <b>Detailed design submitted</b>
          <PartyChip owner="inrange" />
          <span>
            Full EUSDR Exhibit 1.1 package with a compliance certificate.
          </span>
          <When at={submitted} otherwise="Schedule 1 due date" />
          <span className={styles.loop}>
            If Amazon says it is incomplete, we resubmit within 5 Business Days.
          </span>
        </div>
        <div className={styles.drArrow} aria-hidden="true">
          →
        </div>
        <div className={styles.drParallel}>
          <div className={`${styles.drCard} ${styles.ownAmazon}`}>
            <b>Amazon technical review</b>
            <PartyChip owner="amazon" />
            <span className={styles.whose}>Amazon's responsibility</span>
            <span>
              A third-party engineering firm, checked by Amazon's renewable
              energy experts, against the 195-point spec list.
            </span>
            <When at={review} otherwise="Up to 90 Business Days" />
            <span className={styles.loop}>
              Issues raised and closed in BIM360
            </span>
          </div>
          {freehold ? (
            <div className={`${styles.drCard} ${styles.ownAmazon}`}>
              <b>Amazon Real Estate approval</b>
              <PartyChip owner="amazon" />
              <span className={styles.whose}>Amazon's, in place of a landlord</span>
              <span>
                Amazon owns the building, so there is no landlord pack. Amazon Real
                Estate approves the works through its own owned-site route, with a named
                signatory.
              </span>
            </div>
          ) : (
            <div className={styles.drPair}>
              <div className={`${styles.drCard} ${styles.ownInrange}`}>
                <b>Detailed LL pack submitted</b>
                <PartyChip owner="inrange" />
                <span className={styles.whose}>Ours</span>
                <span>
                  The detailed design submission is also the landlord's pack, with
                  anything the landlord or its engineer of record reasonably asks
                  for.
                </span>
              </div>
              <div className={`${styles.drCard} ${styles.ownThird}`}>
                <b>LL sign-off</b>
                <PartyChip owner="third" label="Landlord" />
                <span className={styles.whose}>
                  The landlord's, which Amazon seeks
                </span>
                <span>
                  Consent to the alteration, for example a Licence to Alter, plus
                  any insurer, funder or roof warrantor consents. Amazon uses
                  reasonable endeavours to get it.
                </span>
                <span className={styles.when}>
                  By the Schedule 1 Landlord Approval Date
                </span>
                <span className={styles.loop}>
                  Either party can extend by up to 90 days, or 1 year if both
                  agree. Not needed where Amazon owns the freehold.
                </span>
              </div>
            </div>
          )}
          <div className={`${styles.drCard} ${styles.ownAmazon}`}>
            <b>Insurance review</b>
            <PartyChip owner="amazon" />
            <span className={styles.whose}>
              Amazon's responsibility: its own check, nothing for us to submit
              beyond the detailed design
            </span>
            <span>
              Design checked against Amazon's insurance policy and
              recommendations.
            </span>
            <span className={styles.when}>In the same window</span>
          </div>
        </div>
        <div className={styles.drArrow} aria-hidden="true">
          →
        </div>
        <div className={`${styles.drCard} ${styles.ownAmazon}`}>
          <b>Design approved</b>
          <PartyChip owner="amazon" />
          <span>
            Purchaser Design Approval, due 14 Business Days after Amazon's
            review period ends. There is no deemed approval.
          </span>
          <When
            at={approval}
            otherwise="Up to 104 Business Days after submission"
          />
          <span className={styles.loop}>
            Install can start on the Schedule 1 date, or 30 days after this date
            if later
            {installFrom ? `: no earlier than ${formatDate(installFrom)}` : ""}.
          </span>
        </div>
      </div>
      <div className={styles.clock}>
        <div className={styles.eyebrow}>The clock from submission</div>
        <div className={styles.clockTrack}>
          <div
            className={`${styles.clockSeg} ${styles.segReview}`}
            style={{ left: 0, width: "62%" }}
          >
            Amazon review · 90 BD
          </div>
          <div
            className={`${styles.clockSeg} ${styles.segApproval}`}
            style={{ left: "62%", width: "10%" }}
          >
            +14 BD
          </div>
          <div
            className={`${styles.clockSeg} ${styles.segInstall}`}
            style={{ left: "72%", width: "28%" }}
          >
            +30 days to install start
          </div>
        </div>
        <p className={styles.note}>
          Landlord and design approval date extensions move install start and
          Target COD day for day (MSPPA s4.5(c)). Business Days skip weekends
          and bank holidays in England and Wales.
        </p>
      </div>
    </div>
  );
};
